import { idmsApi } from "./api";
import { API_DEALER_ENDPOINT } from "../../constants/endpoints";
import { convertObjectToQueryParams } from "../../util/convertObjectToQueryParams";
import type { DealerDetailsResponse } from "../../target-manager/models/dealer-details";
import type { DealerSubscriptionHistoryDto } from "../../target-manager/models/dealer-subscription";

// Tier-aware available features for a dealer/product (base tier features + active add-ons)
export type DealerAvailableFeatureDto = {
  id: string;
  name?: string | null;
  description?: string | null;
};

const dealerApi = idmsApi.injectEndpoints({
  endpoints: (builder) => ({
    getDealers: builder.query<any, GetDealership>({
      query: (getDealerships) =>
        `${API_DEALER_ENDPOINT}?${convertObjectToQueryParams(
            getDealerships
        )}`,
    }),

    // New: direct SQL-backed dealers endpoint (I hate stored procedures)
    getDealersDirect: builder.query<any, GetDealership>({
      query: (getDealerships) =>
        `${API_DEALER_ENDPOINT}/query?${convertObjectToQueryParams(
          getDealerships
        )}`,
    }),

    getDmsList: builder.query<any, void>({
        query: () => API_DEALER_ENDPOINT + "/Dms",
    }),

    assignProduct: builder.mutation<any, DealerAssignProductV2>({
        query: (request) => ({
          url: API_DEALER_ENDPOINT + "/assignProduct",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _err, arg) => [
          { type: "ProductDealers", id: arg.productId },
          { type: "DealerFeatures", id: arg.productId },
          // Onboarding creates/assigns the Administrator role and inserts UserProduct rows
          // for the dealer's users, so refresh those lists to reflect it without a relogin.
          "User",
          "UserRole",
          "RoleUser",
          "UserProduct",
          "Role",
          ...arg.dealerIds.map((dealerId) => ({
            type: "AddOns" as const,
            id: `dealer-${dealerId}-product-${arg.productId}`,
          })),
        ],
    }),
    unAssignProduct: builder.mutation<any, DealerUnAssignProductV2>({
      query: (request) => ({
        url: API_DEALER_ENDPOINT + "/UnAssignProduct",
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "ProductDealers", id: arg.productId },
        { type: "DealerFeatures", id: arg.productId },
        // Unassigning deletes the dealer users' UserProduct rows, so refresh user-product lists/counts.
        "UserProduct",
        { type: "AddOns", id: `dealer-${arg.dealerId}-product-${arg.productId}` },
      ],
    }),
    getDealersByProduct: builder.query<any, string>({
      query: (productId) => `${API_DEALER_ENDPOINT}/${productId}`,
      providesTags: (_result, _err, productId) => [
        { type: "ProductDealers", id: productId },
      ],
    }),

    // Total dealers added within the last N days (requires backend support)
    getDealersAddedSince: builder.query<{ count: number }, number>({
      // arg = days
      query: (days) => `${API_DEALER_ENDPOINT}/added-since?days=${days}`,
    }),

    // Recent onboarding activity: Assign (onboard) and Change (tier change)
    getDealerOnboardingActivity: builder.query<any[], number>({
      // arg = days
      query: (days) => `${API_DEALER_ENDPOINT}/onboarding-activity?days=${days}`,
    }),

    // New endpoints for dealer details and subscription history
    getDealerDetails: builder.query<DealerDetailsResponse, number>({
      query: (dealerId) => `${API_DEALER_ENDPOINT}/${dealerId}/details`,
    }),
    getDealerSubscriptionHistory: builder.query<DealerSubscriptionHistoryDto[], number>({
      query: (dealerId) => `${API_DEALER_ENDPOINT}/${dealerId}/subscription-history`,
    }),

    // Tier-aware features available to a dealer for a product (base tier + active add-ons)
    getDealerAvailableFeatures: builder.query<DealerAvailableFeatureDto[], { dealerGuid: string; productId: string }>({
      query: ({ dealerGuid, productId }) => `${API_DEALER_ENDPOINT}/${dealerGuid}/${productId}/features`,
      providesTags: (_result, _err, { productId }) => [
        { type: "DealerFeatures", id: productId },
      ],
    }),

    // Locations under a dealer's DMS Dealer ID
    getDealerLocations: builder.query<DealerLocationDto[], number>({
      query: (dealerId) => `${API_DEALER_ENDPOINT}/${dealerId}/locations`,
    }),

    // Distribution of dealers by location count
    getDealerLocationDistribution: builder.query<DealerLocationDistributionDto[], void>({
      query: () => `${API_DEALER_ENDPOINT}/location-distribution`,
    }),
  }),
});

export const {
  useGetDealersQuery,
  useGetDealersDirectQuery,
  useGetDmsListQuery,
  useAssignProductMutation,
  useUnAssignProductMutation,
  useGetDealersByProductQuery,
  useGetDealerDetailsQuery,
  useGetDealerSubscriptionHistoryQuery
  , useGetDealerLocationsQuery
  , useGetDealersAddedSinceQuery
  , useGetDealerOnboardingActivityQuery
  , useGetDealerLocationDistributionQuery
  , useGetDealerAvailableFeaturesQuery
} = dealerApi;

